/**
 * Contact Lens PDP — vanilla JS controller
 * Handles: gallery (thumbs, zoom, modal, swipe), independent right/left eye prescription
 * selectors with combined-availability recompute, "same prescription" mirroring, per-eye
 * box quantity, live order total, prescription summary, Add to Cart (one cart line per
 * included eye, full prescription stored in line item properties), sticky cart bar,
 * accordion, share, wishlist and recently-viewed (localStorage, no app).
 *
 * Reuses window.AldarCart (already defined in theme.js) for cart AJAX when present;
 * falls back to raw /cart/add.js fetch calls otherwise. Does not modify or depend on
 * any change to existing theme files.
 */
(function () {
  'use strict';
  function stripTrailingZeros(str) { return String(str).replace(/(\d)[.,]00(?!\d)/, '$1'); }

  var root = document.querySelector('[data-cl-pdp]');
  if (!root) return;

  var dataScript = document.querySelector('[data-cl-product-json]');
  var productData = null;
  try {
    productData = dataScript ? JSON.parse(dataScript.textContent) : null;
  } catch (e) {
    productData = null;
  }
  if (!productData) return;

  var scriptTag = document.querySelector('script[data-cl-enable-zoom]');
  var FLAGS = {
    zoom: scriptTag ? scriptTag.getAttribute('data-cl-enable-zoom') === 'true' : true,
    sticky: scriptTag ? scriptTag.getAttribute('data-cl-enable-sticky') === 'true' : true,
    recentlyViewed: scriptTag ? scriptTag.getAttribute('data-cl-enable-recently-viewed') === 'true' : true
  };

  /* The v2 configurator ([data-clv2]) owns the buy flow when it is present.
     The legacy code below finds none of its own elements, so its state stays
     empty - and its updateOrderTotal() was disabling Add to Cart forever. */
  var HAS_CLV2 = !!document.querySelector('[data-clv2]');

  var moneyFormat = productData.moneyFormat || '${{amount}}';
  var optionNames = productData.optionNames || [];
  var maxBoxes = productData.maxBoxes || 6;

  /* =====================================================================
     MONEY FORMATTING — handles every standard Shopify money_format placeholder
     ===================================================================== */
  function formatMoney(cents) {
    if (cents === null || cents === undefined) return '';
    var negative = cents < 0;
    var value = Math.abs(cents) / 100;
    var noDecimals = Math.round(value).toString();
    var withDecimals = value.toFixed(2);
    var decimalParts = withDecimals.split('.');

    function commaSeparate(numStr) {
      return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    function spaceSeparate(numStr) {
      return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    }

    var replacements = {
      amount: withDecimals,
      amount_no_decimals: noDecimals,
      amount_with_comma_separator: commaSeparate(decimalParts[0]) + '.' + decimalParts[1],
      amount_no_decimals_with_comma_separator: commaSeparate(noDecimals),
      amount_with_space_separator: spaceSeparate(decimalParts[0]) + ',' + decimalParts[1],
      amount_no_decimals_with_space_separator: spaceSeparate(noDecimals),
      amount_with_period_and_space_separator: spaceSeparate(decimalParts[0]) + '.' + decimalParts[1]
    };

    var format = moneyFormat || '${{amount}}';
    var result = format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(replacements, key) ? replacements[key] : match;
    });
    return stripTrailingZeros(negative ? '-' + result : result);
  }

  function findVariantById(id) {
    id = String(id);
    for (var i = 0; i < productData.variants.length; i++) {
      if (String(productData.variants[i].id) === id) return productData.variants[i];
    }
    return null;
  }

  function variantForOptions(options) {
    for (var i = 0; i < productData.variants.length; i++) {
      var v = productData.variants[i];
      var match = true;
      for (var j = 0; j < options.length; j++) {
        if (options[j] !== undefined && v.options[j] !== options[j]) { match = false; break; }
      }
      if (match) return v;
    }
    return null;
  }

  /* =====================================================================
     STATE — one independent state object per eye
     ===================================================================== */
  var state = {
    mode: 'both',
    samePrescription: false,
    right: { selectedOptions: [], boxes: 1, variant: null, included: true },
    left: { selectedOptions: [], boxes: 1, variant: null, included: true }
  };

  function eyeState(eye) {
    return eye === 'left' ? state.left : state.right;
  }

  /* =====================================================================
     GALLERY
     ===================================================================== */
  var mainImageEl = document.querySelector('[data-cl-main-image]');
  var slides = Array.prototype.slice.call(document.querySelectorAll('[data-cl-media-slide]'));
  var thumbs = Array.prototype.slice.call(document.querySelectorAll('[data-cl-thumb]'));
  var dots = Array.prototype.slice.call(document.querySelectorAll('[data-cl-media-dot]'));

  function showMediaById(mediaId) {
    if (!mediaId) return;
    mediaId = String(mediaId);
    slides.forEach(function (slide) {
      var match = slide.getAttribute('data-media-id') === mediaId;
      slide.hidden = !match;
      slide.classList.toggle('active', match);
    });
    thumbs.forEach(function (thumb) {
      var match = thumb.getAttribute('data-media-id') === mediaId;
      thumb.classList.toggle('active', match);
      thumb.setAttribute('aria-selected', match ? 'true' : 'false');
    });
    var idx = slides.findIndex(function (s) { return s.getAttribute('data-media-id') === mediaId; });
    if (idx !== -1) dots.forEach(function (dot, i) { dot.classList.toggle('active', i === idx); });
  }

  thumbs.forEach(function (thumb) {
    thumb.addEventListener('click', function () {
      showMediaById(thumb.getAttribute('data-media-id'));
    });
  });

  if (FLAGS.zoom && mainImageEl) {
    mainImageEl.addEventListener('mousemove', function (e) {
      if (!mainImageEl.classList.contains('zoomed')) return;
      var rect = mainImageEl.getBoundingClientRect();
      var x = ((e.clientX - rect.left) / rect.width) * 100;
      var y = ((e.clientY - rect.top) / rect.height) * 100;
      var img = mainImageEl.querySelector('.cl-media-slide.active .cl-media-image');
      if (img) img.style.transformOrigin = x + '% ' + y + '%';
    });
    var zoomToggle = document.querySelector('[data-cl-zoom-toggle]');
    if (zoomToggle) {
      zoomToggle.addEventListener('click', function (e) {
        e.stopPropagation();
        mainImageEl.classList.toggle('zoomed');
      });
    }
    mainImageEl.addEventListener('mouseleave', function () {
      mainImageEl.classList.remove('zoomed');
    });
  }

  var modal = document.querySelector('[data-cl-gallery-modal]');
  var modalStage = document.querySelector('[data-cl-modal-stage]');
  var modalIndex = 0;

  function openModalAt(index) {
    if (!modal || !modalStage || slides.length === 0) return;
    modalIndex = ((index % slides.length) + slides.length) % slides.length;
    var slide = slides[modalIndex];
    modalStage.innerHTML = '';
    var clone = slide.cloneNode(true);
    clone.hidden = false;
    clone.removeAttribute('hidden');
    modalStage.appendChild(clone);
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
  }
  function closeModal() {
    if (!modal) return;
    modal.hidden = true;
    document.body.style.overflow = '';
  }
  var openModalBtn = document.querySelector('[data-cl-open-modal]');
  if (openModalBtn) {
    openModalBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      var activeIdx = slides.findIndex(function (s) { return s.classList.contains('active'); });
      openModalAt(activeIdx === -1 ? 0 : activeIdx);
    });
  }
  var modalClose = document.querySelector('[data-cl-close-modal]');
  if (modalClose) modalClose.addEventListener('click', closeModal);
  if (modal) {
    modal.addEventListener('click', function (e) {
      if (e.target === modal) closeModal();
    });
  }
  var modalPrev = document.querySelector('[data-cl-modal-prev]');
  var modalNext = document.querySelector('[data-cl-modal-next]');
  if (modalPrev) modalPrev.addEventListener('click', function () { openModalAt(modalIndex - 1); });
  if (modalNext) modalNext.addEventListener('click', function () { openModalAt(modalIndex + 1); });
  document.addEventListener('keydown', function (e) {
    if (!modal || modal.hidden) return;
    if (e.key === 'Escape') closeModal();
    if (e.key === 'ArrowLeft') openModalAt(modalIndex - 1);
    if (e.key === 'ArrowRight') openModalAt(modalIndex + 1);
  });

  if (mainImageEl) {
    var touchStartX = 0;
    var touchEndX = 0;
    mainImageEl.addEventListener('touchstart', function (e) {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });
    mainImageEl.addEventListener('touchend', function (e) {
      touchEndX = e.changedTouches[0].screenX;
      var delta = touchEndX - touchStartX;
      if (Math.abs(delta) < 40 || slides.length < 2) return;
      var activeIdx = slides.findIndex(function (s) { return s.classList.contains('active'); });
      var nextIdx = delta < 0 ? activeIdx + 1 : activeIdx - 1;
      nextIdx = ((nextIdx % slides.length) + slides.length) % slides.length;
      showMediaById(slides[nextIdx].getAttribute('data-media-id'));
    }, { passive: true });
  }

  /* =====================================================================
     EYE MODE (both / right only / left only)
     ===================================================================== */
  var eyeToggleButtons = Array.prototype.slice.call(document.querySelectorAll('[data-cl-eye-mode]'));
  var sameWrap = document.querySelector('[data-cl-same-prescription-wrap]');
  var sameCheckbox = document.querySelector('[data-cl-same-prescription]');
  var eyeCols = Array.prototype.slice.call(document.querySelectorAll('[data-cl-eye-col]'));
  var boxCols = Array.prototype.slice.call(document.querySelectorAll('[data-cl-box-col]'));

  function applyEyeMode(mode) {
    state.mode = mode;
    eyeToggleButtons.forEach(function (btn) {
      var active = btn.getAttribute('data-cl-eye-mode') === mode;
      btn.classList.toggle('active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    var showRight = mode === 'both' || mode === 'right';
    var showLeft = mode === 'both' || mode === 'left';

    eyeCols.forEach(function (col) {
      var eye = col.getAttribute('data-eye');
      var show = eye === 'right' ? showRight : showLeft;
      col.hidden = !show;
    });
    boxCols.forEach(function (col) {
      var eye = col.getAttribute('data-eye');
      var show = eye === 'right' ? showRight : showLeft;
      col.hidden = !show;
    });

    state.right.included = showRight;
    state.left.included = showLeft;

    if (sameWrap) sameWrap.hidden = mode !== 'both';
    if (mode !== 'both' && sameCheckbox && sameCheckbox.checked) {
      sameCheckbox.checked = false;
      setLeftDisabled(false);
    }

    updateOrderTotal();
    updatePrescriptionSummary();
  }

  eyeToggleButtons.forEach(function (btn) {
    btn.addEventListener('click', function () {
      applyEyeMode(btn.getAttribute('data-cl-eye-mode'));
    });
  });

  function setLeftDisabled(disabled) {
    document.querySelectorAll('[data-cl-option-select][data-eye="left"]').forEach(function (sel) {
      sel.disabled = disabled;
    });
  }

  function mirrorRightToLeft() {
    document.querySelectorAll('[data-cl-option-select][data-eye="right"]').forEach(function (rightSel) {
      var optionIndex = rightSel.getAttribute('data-option-index');
      var leftSel = document.querySelector('[data-cl-option-select][data-eye="left"][data-option-index="' + optionIndex + '"]');
      if (leftSel) {
        leftSel.value = rightSel.value;
      }
    });
    syncEyeSelectedOptions('left');
    refreshEyeAvailability('left');
    applyEyeVariant('left');
  }

  if (sameCheckbox) {
    sameCheckbox.addEventListener('change', function () {
      state.samePrescription = sameCheckbox.checked;
      setLeftDisabled(sameCheckbox.checked);
      if (sameCheckbox.checked) mirrorRightToLeft();
      updateOrderTotal();
      updatePrescriptionSummary();
    });
  }

  /* =====================================================================
     PRESCRIPTION OPTION SELECTS (per eye, combined-availability recompute)
     ===================================================================== */
  function syncEyeSelectedOptions(eye) {
    var es = eyeState(eye);
    var selects = document.querySelectorAll('[data-cl-option-select][data-eye="' + eye + '"]');
    var arr = [];
    selects.forEach(function (sel) {
      var idx = parseInt(sel.getAttribute('data-option-index'), 10) - 1;
      arr[idx] = sel.value;
    });
    es.selectedOptions = arr;
  }

  function isValueAvailable(eye, optionIndex, value) {
    var es = eyeState(eye);
    for (var i = 0; i < productData.variants.length; i++) {
      var v = productData.variants[i];
      if (v.options[optionIndex] !== value) continue;
      var ok = true;
      for (var j = 0; j < es.selectedOptions.length; j++) {
        if (j === optionIndex) continue;
        if (es.selectedOptions[j] && v.options[j] !== es.selectedOptions[j]) { ok = false; break; }
      }
      if (ok && v.available) return true;
    }
    return false;
  }

  function refreshEyeAvailability(eye) {
    var selects = document.querySelectorAll('[data-cl-option-select][data-eye="' + eye + '"]');
    selects.forEach(function (sel) {
      var optionIndex = parseInt(sel.getAttribute('data-option-index'), 10) - 1;
      Array.prototype.slice.call(sel.options).forEach(function (opt) {
        if (!opt.value) return;
        var available = isValueAvailable(eye, optionIndex, opt.value);
        opt.disabled = !available;
        opt.textContent = available ? opt.value : opt.value + ' — Out of stock';
      });
    });
  }

  function applyEyeVariant(eye) {
    var es = eyeState(eye);
    es.variant = variantForOptions(es.selectedOptions);
  }

  document.querySelectorAll('[data-cl-option-select]').forEach(function (sel) {
    sel.addEventListener('change', function () {
      var eye = sel.getAttribute('data-eye');
      syncEyeSelectedOptions(eye);
      refreshEyeAvailability(eye);
      applyEyeVariant(eye);

      if (eye === 'right' && sameCheckbox && sameCheckbox.checked) {
        mirrorRightToLeft();
      }

      updateOrderTotal();
      updatePrescriptionSummary();
      hideValidationMessage();
    });
  });

  /* =====================================================================
     BOX QUANTITY (per eye)
     ===================================================================== */
  function setBoxes(eye, qty) {
    qty = Math.max(1, Math.min(maxBoxes, qty));
    eyeState(eye).boxes = qty;
    document.querySelectorAll('[data-cl-box-stepper][data-eye="' + eye + '"] [data-cl-box-input]').forEach(function (el) {
      el.value = qty;
    });
    updateOrderTotal();
    updatePrescriptionSummary();
  }

  document.querySelectorAll('[data-cl-box-stepper]').forEach(function (stepper) {
    var eye = stepper.getAttribute('data-eye');
    var input = stepper.querySelector('[data-cl-box-input]');
    var minus = stepper.querySelector('[data-cl-box-minus]');
    var plus = stepper.querySelector('[data-cl-box-plus]');

    if (minus) minus.addEventListener('click', function () { setBoxes(eye, eyeState(eye).boxes - 1); });
    if (plus) plus.addEventListener('click', function () { setBoxes(eye, eyeState(eye).boxes + 1); });
    if (input) {
      input.addEventListener('change', function () {
        var val = parseInt(input.value, 10);
        if (isNaN(val)) val = 1;
        setBoxes(eye, val);
      });
    }
  });

  /* =====================================================================
     ORDER TOTAL + PRESCRIPTION SUMMARY
     ===================================================================== */
  function updateOrderTotal() {
    if (HAS_CLV2) return;   /* v2 handles the total AND the button state */
    var rightTotal = (state.right.included && state.right.variant) ? state.right.variant.price * state.right.boxes : 0;
    var leftTotal = (state.left.included && state.left.variant) ? state.left.variant.price * state.left.boxes : 0;
    var total = rightTotal + leftTotal;

    document.querySelectorAll('[data-cl-order-total]').forEach(function (el) {
      el.textContent = formatMoney(total);
    });
    document.querySelectorAll('[data-cl-sticky-price]').forEach(function (el) {
      el.textContent = formatMoney(total);
    });

    var parts = [];
    if (state.right.included) parts.push('Right: ' + state.right.boxes + ' box' + (state.right.boxes > 1 ? 'es' : ''));
    if (state.left.included) parts.push('Left: ' + state.left.boxes + ' box' + (state.left.boxes > 1 ? 'es' : ''));
    document.querySelectorAll('[data-cl-order-note]').forEach(function (el) {
      el.textContent = parts.join(' · ');
    });

    document.querySelectorAll('[data-cl-add-to-cart]').forEach(function (btn) {
      var anyIncluded = state.right.included || state.left.included;
      var anyValid = (state.right.included && state.right.variant && state.right.variant.available) ||
        (state.left.included && state.left.variant && state.left.variant.available);
      btn.disabled = !(anyIncluded && anyValid);
    });
  }

  function summarizeEye(eye) {
    var es = eyeState(eye);
    if (!es.included) return '';
    var bits = [];
    optionNames.forEach(function (name, i) {
      if (es.selectedOptions[i]) bits.push(name + ' ' + es.selectedOptions[i]);
    });
    var label = eye === 'right' ? 'Right Eye' : 'Left Eye';
    return label + ': ' + bits.join(', ') + ' (' + es.boxes + ' box' + (es.boxes > 1 ? 'es' : '') + ')';
  }

  function updatePrescriptionSummary() {
    var lines = [];
    if (state.right.included) lines.push(summarizeEye('right'));
    if (state.left.included) lines.push(summarizeEye('left'));
    var text = lines.join(' | ');

    document.querySelectorAll('[data-cl-prescription-summary]').forEach(function (el) {
      el.textContent = text;
      el.classList.toggle('has-content', text.length > 0);
    });
    document.querySelectorAll('[data-cl-sticky-summary]').forEach(function (el) {
      el.textContent = text;
    });
  }

  /* =====================================================================
     VALIDATION MESSAGE
     ===================================================================== */
  function showValidationMessage(message) {
    document.querySelectorAll('[data-cl-validation-message]').forEach(function (el) {
      el.textContent = message;
      el.hidden = false;
    });
  }
  function hideValidationMessage() {
    document.querySelectorAll('[data-cl-validation-message]').forEach(function (el) {
      el.hidden = true;
    });
  }

  /* =====================================================================
     TOAST
     ===================================================================== */
  var toastEl = null;
  function toast(message) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'cl-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    toastEl.classList.add('visible');
    clearTimeout(toastEl._t);
    toastEl._t = setTimeout(function () { toastEl.classList.remove('visible'); }, 2200);
  }

  /* =====================================================================
     ADD TO CART — one cart line per included eye, full prescription stored
     as line item properties.
     ===================================================================== */
  function cartAdd(variantId, qty, properties) {
    if (window.AldarCart && typeof window.AldarCart.add === 'function') {
      return window.AldarCart.add(variantId, qty, properties);
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty, properties: properties })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (err) { throw err; });
      return r.json();
    });
  }

  function refreshCartBadge() {
    if (window.AldarCart && typeof window.AldarCart.refreshBadge === 'function') {
      window.AldarCart.refreshBadge();
    }
  }

  function openCartDrawer() {
    if (window.AldarCartDrawer && typeof window.AldarCartDrawer.refresh === 'function') {
      window.AldarCartDrawer.refresh().then(window.AldarCartDrawer.open);
      return true;
    }
    return false;
  }

  function buildProperties(eye) {
    var es = eyeState(eye);
    var props = { Eye: eye === 'right' ? 'Right' : 'Left' };
    optionNames.forEach(function (name, i) {
      if (es.selectedOptions[i]) props[name] = es.selectedOptions[i];
    });
    props.Boxes = String(es.boxes);
    return props;
  }

  document.addEventListener('click', function (e) {
    if (HAS_CLV2) return;
    var addBtn = e.target.closest('[data-cl-add-to-cart]');
    if (!addBtn || addBtn.disabled) return;

    var jobs = [];
    if (state.right.included) {
      if (!state.right.variant || !state.right.variant.available) {
        showValidationMessage('Please complete a valid prescription for the right eye.');
        return;
      }
      jobs.push({ eye: 'right', variantId: state.right.variant.id, qty: state.right.boxes, properties: buildProperties('right') });
    }
    if (state.left.included) {
      if (!state.left.variant || !state.left.variant.available) {
        showValidationMessage('Please complete a valid prescription for the left eye.');
        return;
      }
      jobs.push({ eye: 'left', variantId: state.left.variant.id, qty: state.left.boxes, properties: buildProperties('left') });
    }
    if (jobs.length === 0) {
      showValidationMessage('Please select at least one eye.');
      return;
    }

    hideValidationMessage();
    document.querySelectorAll('[data-cl-add-to-cart]').forEach(function (btn) { btn.classList.add('is-loading'); });

    var chain = Promise.resolve();
    jobs.forEach(function (job) {
      chain = chain.then(function () {
        return cartAdd(job.variantId, job.qty, job.properties);
      });
    });
    chain
      .then(function () {
        refreshCartBadge();
        if (!openCartDrawer()) toast('Added to cart');
      })
      .catch(function () {
        toast('Could not add to cart — please try again');
      })
      .finally(function () {
        document.querySelectorAll('[data-cl-add-to-cart]').forEach(function (btn) { btn.classList.remove('is-loading'); });
      });
  });

  /* =====================================================================
     WISHLIST (localStorage, no app)
     ===================================================================== */
  var WISHLIST_KEY = 'aldar_wishlist';
  function getWishlist() {
    try { return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || []; } catch (e) { return []; }
  }
  function setWishlist(list) {
    try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(list)); } catch (e) { /* storage unavailable */ }
  }
  function syncWishlistButton(btn) {
    var handle = btn.getAttribute('data-product-handle');
    var active = getWishlist().indexOf(handle) !== -1;
    btn.classList.toggle('active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
  }
  document.querySelectorAll('[data-cl-wishlist]').forEach(function (btn) {
    syncWishlistButton(btn);
    btn.addEventListener('click', function () {
      var handle = btn.getAttribute('data-product-handle');
      var list = getWishlist();
      var idx = list.indexOf(handle);
      if (idx === -1) { list.push(handle); toast('Added to wishlist'); }
      else { list.splice(idx, 1); toast('Removed from wishlist'); }
      setWishlist(list);
      syncWishlistButton(btn);
      btn.classList.add('is-pulsing');
      setTimeout(function () { btn.classList.remove('is-pulsing'); }, 350);
    });
  });

  /* =====================================================================
     SHARE
     ===================================================================== */
  document.querySelectorAll('[data-cl-share-trigger]').forEach(function (trigger) {
    trigger.addEventListener('click', function (e) {
      e.stopPropagation();
      var title = trigger.getAttribute('data-share-title');
      var url = trigger.getAttribute('data-share-url');
      var text = trigger.getAttribute('data-share-text');

      if (navigator.share) {
        navigator.share({ title: title, text: text, url: url }).catch(function () { /* user cancelled */ });
        return;
      }
      var menu = trigger.parentElement.querySelector('[data-cl-share-menu]');
      if (menu) {
        var isHidden = menu.hasAttribute('hidden');
        document.querySelectorAll('[data-cl-share-menu]').forEach(function (m) { m.setAttribute('hidden', ''); });
        if (isHidden) menu.removeAttribute('hidden');
        else menu.setAttribute('hidden', '');
        trigger.setAttribute('aria-expanded', isHidden ? 'true' : 'false');
      }
    });
  });
  document.addEventListener('click', function (e) {
    if (!e.target.closest('[data-cl-share]')) {
      document.querySelectorAll('[data-cl-share-menu]').forEach(function (m) { m.setAttribute('hidden', ''); });
    }
    var copyBtn = e.target.closest('[data-cl-share-copy]');
    if (copyBtn) {
      var url = copyBtn.closest('[data-cl-share]').querySelector('[data-cl-share-trigger]').getAttribute('data-share-url');
      if (navigator.clipboard) {
        navigator.clipboard.writeText(url).then(function () { toast('Link copied'); });
      }
    }
  });

  /* =====================================================================
     ACCORDION
     ===================================================================== */
  document.querySelectorAll('[data-cl-accordion-trigger]').forEach(function (trigger) {
    trigger.addEventListener('click', function () {
      var expanded = trigger.getAttribute('aria-expanded') === 'true';
      var panel = trigger.parentElement.querySelector('[data-cl-accordion-panel]');
      trigger.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      if (panel) panel.hidden = expanded;
    });
  });

  /* =====================================================================
     STICKY ADD-TO-CART BAR
     ===================================================================== */
  if (FLAGS.sticky) {
    var stickyBar = document.querySelector('[data-cl-sticky-bar]');
    var anchor = document.querySelector('[data-cl-buy-buttons] [data-cl-add-to-cart]');
    if (stickyBar && anchor && 'IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          stickyBar.classList.toggle('visible', show);
          stickyBar.setAttribute('aria-hidden', show ? 'false' : 'true');
        });
      }, { threshold: 0 });
      observer.observe(anchor);
    }
  }

  /* =====================================================================
     RECENTLY VIEWED (localStorage, no app)
     ===================================================================== */
  if (FLAGS.recentlyViewed) {
    var RV_KEY = 'aldar_recently_viewed';
    var MAX_ITEMS = 8;

    function getRecentlyViewed() {
      try { return JSON.parse(localStorage.getItem(RV_KEY)) || []; } catch (e) { return []; }
    }
    function setRecentlyViewed(list) {
      try { localStorage.setItem(RV_KEY, JSON.stringify(list)); } catch (e) { /* storage unavailable */ }
    }

    function escapeHtml(str) {
      var div = document.createElement('div');
      div.textContent = str == null ? '' : String(str);
      return div.innerHTML;
    }

    function renderRecentlyViewed() {
      var list = getRecentlyViewed().filter(function (item) { return item.handle !== productData.handle; });
      var container = document.querySelector('[data-cl-recently-viewed-grid]');
      var section = document.querySelector('[data-cl-recently-viewed]');
      if (!container || !section) return;
      if (list.length === 0) { section.hidden = true; return; }

      container.innerHTML = '';
      list.slice(0, 4).forEach(function (item) {
        var a = document.createElement('a');
        a.className = 'product reveal in';
        a.href = item.url;
        var priceHtml = '<span class="now">' + escapeHtml(item.priceFormatted || '') + '</span>';
        a.innerHTML =
          '<div class="product-image">' +
            '<img class="product-image-main" src="' + escapeHtml(item.image || '') + '" alt="' + escapeHtml(item.title) + '" loading="lazy" width="300" height="300">' +
          '</div>' +
          '<div class="product-info">' +
            (item.vendor ? '<div class="product-brand">' + escapeHtml(item.vendor) + '</div>' : '') +
            '<h3 class="product-name">' + escapeHtml(item.title) + '</h3>' +
            '<div class="product-price">' + priceHtml + '</div>' +
          '</div>';
        container.appendChild(a);
      });
      section.hidden = false;
    }

    function recordCurrentProduct() {
      var list = getRecentlyViewed().filter(function (item) { return item.handle !== productData.handle; });
      var basePrice = productData.variants && productData.variants.length ? productData.variants[0].price : null;
      list.unshift({
        handle: productData.handle,
        title: productData.title,
        vendor: productData.vendor,
        url: productData.url,
        image: productData.featuredImage,
        priceFormatted: formatMoney(basePrice)
      });
      if (list.length > MAX_ITEMS) list = list.slice(0, MAX_ITEMS);
      setRecentlyViewed(list);
    }

    renderRecentlyViewed();
    recordCurrentProduct();
  }

  /* =====================================================================
     INIT
     ===================================================================== */
  syncEyeSelectedOptions('right');
  syncEyeSelectedOptions('left');
  refreshEyeAvailability('right');
  refreshEyeAvailability('left');
  applyEyeVariant('right');
  applyEyeVariant('left');
  applyEyeMode('both');
})();

/* Default: in Both Eyes mode the customer fills one eye only - mirror it to the other */
(function () {
  var c = document.querySelector('[data-cl-same-prescription]');
  if (c && c.checked) { c.dispatchEvent(new Event('change')); }
})();

/* =====================================================================
   RECENTLY VIEWED - standalone failsafe module
   Runs independently of the main controller, so a script error anywhere
   else on the page can never stop Recently Viewed from working.
   Idempotent: safe even when the main module already ran.
   ===================================================================== */
(function () {
  'use strict';
  try {
    var enableTag = document.querySelector('script[data-aldar-enable-recently-viewed], script[data-cl-enable-recently-viewed]');
    if (enableTag) {
      var flag = enableTag.getAttribute('data-aldar-enable-recently-viewed') || enableTag.getAttribute('data-cl-enable-recently-viewed');
      if (flag === 'false') return;
    }

    var dataScript = document.querySelector('[data-aldar-product-json], [data-cl-product-json]');
    if (!dataScript) return;
    var pd = null;
    try { pd = JSON.parse(dataScript.textContent); } catch (e) { return; }
    if (!pd || !pd.handle) return;

    var section = document.querySelector('[data-aldar-recently-viewed], [data-cl-recently-viewed]');
    var grid = document.querySelector('[data-aldar-recently-viewed-grid], [data-cl-recently-viewed-grid]');

    var KEY = 'aldar_recently_viewed';
    var MAX = 8;

    function read() {
      try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch (e) { return []; }
    }
    function write(list) {
      try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {}
    }
    function money(cents) {
      if (cents === null || cents === undefined) return '';
      var v = (cents / 100).toFixed(2);
      var fmt = pd.moneyFormat || '{{amount}}';
      var noDec = (cents / 100).toFixed(0);
      var comma = v.replace('.', ',');
      return fmt
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDec)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, comma)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDec)
        .replace(/\{\{\s*amount\s*\}\}/g, v)
        .replace(/<[^>]*>/g, '').replace(/(\d)[.,]00(?!\d)/, '$1');
    }
    function esc(str) {
      var d = document.createElement('div');
      d.textContent = str == null ? '' : String(str);
      return d.innerHTML;
    }

    /* 1) record this product (idempotent) */
    var v0 = (pd.variants && pd.variants[0]) || {};
    var list = read().filter(function (i) { return i && i.handle !== pd.handle; });
    list.unshift({
      handle: pd.handle,
      title: pd.title,
      vendor: pd.vendor,
      url: pd.url,
      image: pd.featuredImage,
      priceFormatted: money(v0.price),
      compareAtPriceFormatted: v0.compareAtPrice && v0.compareAtPrice > v0.price ? money(v0.compareAtPrice) : null
    });
    if (list.length > MAX) list = list.slice(0, MAX);
    write(list);

    /* 2) render previous products (excluding the current one) */
    if (!section || !grid) return;
    var show = read().filter(function (i) { return i && i.handle !== pd.handle; });
    if (show.length === 0) { section.hidden = true; return; }

    grid.innerHTML = '';
    show.slice(0, 4).forEach(function (item) {
      var a = document.createElement('a');
      a.className = 'product reveal in';
      a.href = item.url;
      var priceHtml = '<span class="now">' + esc(item.priceFormatted || '') + '</span>';
      if (item.compareAtPriceFormatted) {
        priceHtml = '<span class="was">' + esc(item.compareAtPriceFormatted) + '</span>' + priceHtml;
      }
      a.innerHTML =
        '<div class="product-image">' +
          '<img class="product-image-main" src="' + esc(item.image || '') + '" alt="' + esc(item.title) + '" loading="lazy" width="300" height="300">' +
        '</div>' +
        '<div class="product-info">' +
          (item.vendor ? '<div class="product-brand">' + esc(item.vendor) + '</div>' : '') +
          '<h3 class="product-name">' + esc(item.title) + '</h3>' +
          '<div class="product-price">' + priceHtml + '</div>' +
        '</div>';
      grid.appendChild(a);
    });
    section.hidden = false;
  } catch (e) { /* never break the page */ }
})();

/* =====================================================================
   CLV2 - eyewa-style configurator controller
   Same-prescription switch + Left/Right (or Both) table.
   Takes over the buy flow when the new markup is present; the legacy
   configurator code above simply finds none of its old elements.
   ===================================================================== */
(function () {
  'use strict';
  var root = document.querySelector('[data-clv2]');
  if (!root) return;

  var dataScript = document.querySelector('[data-cl-product-json]');
  if (!dataScript) return;
  var pd = null;
  try { pd = JSON.parse(dataScript.textContent); } catch (e) { return; }
  if (!pd || !pd.variants) return;

  var sameToggle = root.querySelector('[data-clv2-same]');
  var totalEl = document.querySelector('[data-cl-order-total]');
  var noteEl = document.querySelector('[data-cl-order-note]');
  var optionCount = (pd.optionNames || []).length;

  function fmtMoney(cents) {
    if (cents === null || cents === undefined) return '';
    var v = (cents / 100).toFixed(2);
    var noDec = (cents / 100).toFixed(0);
    var comma = v.replace('.', ',');
    var out = (pd.moneyFormat || '{{amount}}')
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDec)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, comma)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDec)
      .replace(/\{\{\s*amount\s*\}\}/g, v)
      .replace(/<[^>]*>/g, '');
    return out.replace(/(\d)[.,]00(?!\d)/, '$1');
  }

  function isSame() { return sameToggle ? sameToggle.checked : true; }

  function selectionFor(eye) {
    var sel = [];
    var complete = true;
    for (var i = 0; i < optionCount; i++) {
      var el = root.querySelector('[data-clv2-select][data-eye="' + eye + '"][data-option-index="' + i + '"]');
      var val = el ? el.value : '';
      if (!val) complete = false;
      sel[i] = val;
    }
    return { values: sel, complete: complete };
  }

  function resolveVariant(values) {
    return pd.variants.find(function (v) {
      if (!v.options) return false;
      for (var i = 0; i < optionCount; i++) {
        if (String(v.options[i]) !== String(values[i])) return false;
      }
      return true;
    }) || null;
  }

  /* Extra prescription rows (Cylinder, Axis...) configured in the Theme Editor.
     They are not variants: their values travel as line item properties. */
  function extrasFor(eye) {
    var props = {};
    var missing = [];
    root.querySelectorAll('[data-clv2-extra][data-eye="' + eye + '"]').forEach(function (sel) {
      var label = sel.getAttribute('data-field-label') || 'Prescription';
      var required = sel.getAttribute('data-required') === 'true';
      if (sel.value) props[label] = sel.value;
      else if (required) missing.push(label);
    });
    return { props: props, missing: missing };
  }

  function powerLabel() {
    var cell = root.querySelector('.clv2-row:not(.clv2-row--head):not(.clv2-row--extra) .clv2-cell--label');
    return cell ? cell.textContent.trim() : '';
  }

  function qtyFor(eye) {
    var input = root.querySelector('[data-clv2-stepper][data-eye="' + eye + '"] [data-clv2-qty]');
    var n = input ? parseInt(input.value, 10) : 1;
    return isNaN(n) || n < 1 ? 1 : n;
  }

  function setNote(msg) {
    if (noteEl) {
      noteEl.textContent = msg || '';
      noteEl.classList.toggle('is-error', !!msg);
    }
    document.querySelectorAll('[data-cl-validation-message]').forEach(function (el) {
      el.textContent = msg || '';
      el.hidden = !msg;
    });
  }

  /* Add to Cart stays clickable as long as SOMETHING on this product can be
     bought. If the prescription is incomplete we tell the customer what is
     missing on click - a dead grey button explains nothing. */
  function syncButtons() {
    var anyBuyable = pd.variants.some(function (v) { return v.available !== false; });
    document.querySelectorAll('[data-cl-add-to-cart]').forEach(function (btn) {
      btn.disabled = !anyBuyable;
      var label = btn.querySelector('[data-cl-add-to-cart-label]');
      if (label) label.textContent = anyBuyable ? 'Add to Cart' : 'Sold Out';
    });
  }

  function buildLines() {
    var lines = [];
    if (isSame()) {
      var s = selectionFor('both');
      var ex = extrasFor('both');
      var need = [];
      if (!s.complete) need.push(powerLabel() || (pd.optionNames || ['prescription']).join(', '));
      need = need.concat(ex.missing);
      if (need.length) return { error: 'Please select ' + need.join(', ') + '.' };
      var v = resolveVariant(s.values);
      if (!v) return { error: 'This combination is unavailable.' };
      if (v.available === false) return { error: 'This option is sold out.' };
      var props = { 'Eyes': 'Both eyes' };
      (pd.optionNames || []).forEach(function (name, i) { props[name] = s.values[i]; });
      Object.keys(ex.props).forEach(function (k) { props[k] = ex.props[k]; });
      lines.push({ id: v.id, quantity: qtyFor('both'), properties: props });
    } else {
      var incomplete = [];
      ['left', 'right'].forEach(function (eye) {
        var s = selectionFor(eye);
        var ex = extrasFor(eye);
        var eyeName = eye === 'left' ? 'Left (OS)' : 'Right (OD)';
        var touched = s.values.some(function (x) { return !!x; }) || Object.keys(ex.props).length > 0;
        if (!touched) return;                       /* eye left completely empty = not ordering it */
        var need = [];
        if (!s.complete) need.push(powerLabel() || 'prescription');
        need = need.concat(ex.missing);
        if (need.length) { incomplete.push(eyeName + ': ' + need.join(', ')); return; }
        var v = resolveVariant(s.values);
        if (!v || v.available === false) { incomplete.push(eyeName + ': this option is unavailable'); return; }
        var props = { 'Eye': eyeName };
        (pd.optionNames || []).forEach(function (name, i) { props[name] = s.values[i]; });
        Object.keys(ex.props).forEach(function (k) { props[k] = ex.props[k]; });
        lines.push({ id: v.id, quantity: qtyFor(eye), properties: props });
      });
      if (incomplete.length) return { error: 'Please complete ' + incomplete.join('; ') + '.' };
      if (lines.length === 0) return { error: 'Please select a prescription for at least one eye.' };
    }
    return { lines: lines };
  }

  function updateTotal() {
    syncButtons();
    if (!totalEl) return;
    var built = buildLines();
    if (built.error || !built.lines) {
      var base = pd.variants[0] ? pd.variants[0].price : 0;
      totalEl.textContent = fmtMoney(base);
      setNote('');
      return;
    }
    var total = 0;
    var ok = true;
    built.lines.forEach(function (line) {
      var v = pd.variants.find(function (x) { return x.id === line.id; });
      if (!v) { ok = false; return; }
      total += v.price * line.quantity;
    });
    if (ok) { totalEl.textContent = fmtMoney(total); setNote(''); }
  }

  /* switch: swap columns */
  if (sameToggle) {
    sameToggle.addEventListener('change', function () {
      root.classList.toggle('is-same', sameToggle.checked);
      updateTotal();
    });
  }

  /* selects */
  root.querySelectorAll('[data-clv2-select], [data-clv2-extra]').forEach(function (sel) {
    sel.addEventListener('change', updateTotal);
  });

  /* steppers */
  root.querySelectorAll('[data-clv2-stepper]').forEach(function (stepper) {
    var input = stepper.querySelector('[data-clv2-qty]');
    var max = parseInt(root.getAttribute('data-max-boxes'), 10) || 6;
    stepper.querySelectorAll('button[data-step]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var n = parseInt(input.value, 10) || 1;
        n += parseInt(btn.getAttribute('data-step'), 10);
        if (n < 1) n = 1;
        if (n > max) n = max;
        input.value = n;
        updateTotal();
      });
    });
  });

  /* add to cart: capture phase so the legacy handler never runs */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-cl-add-to-cart]');
    if (!btn) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (btn.disabled || btn.classList.contains('is-loading')) return;

    var built = buildLines();
    if (built.error) {
      setNote(built.error);
      root.scrollIntoView({ behavior: 'smooth', block: 'center' });
      root.classList.add('clv2-needs-attention');
      setTimeout(function () { root.classList.remove('clv2-needs-attention'); }, 1600);
      return;
    }
    setNote('');
    btn.classList.add('is-loading');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: built.lines })
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (j) { throw new Error(j.description || 'Could not add to cart'); });
        return r.json();
      })
      .then(function () {
        if (window.AldarCart && typeof window.AldarCart.refreshBadge === 'function') window.AldarCart.refreshBadge();
        if (window.AldarCartDrawer && typeof window.AldarCartDrawer.refresh === 'function') {
          window.AldarCartDrawer.refresh().then(window.AldarCartDrawer.open);
        }
      })
      .catch(function (err) { setNote(err.message || 'Could not add to cart.'); })
      .then(function () { btn.classList.remove('is-loading'); });
  }, true);

  if (sameToggle) root.classList.toggle('is-same', sameToggle.checked);
  updateTotal();
})();